// UX-6c F2 — the FIRST-RUN MODEL, derived (never a stored wizard). Each step's
// done/pending is computed from data the existing payloads already carry (F1/R4):
//   * teacher: the workspace dashboard's `readiness` flags + latest_lesson / review;
//   * admin: the onboarding status' `onboarding_steps` booleans.
// Steps auto-complete as reality happens — there are NO step-tracking writes. When
// every step is done the model reports complete and the surface hides itself.

// Each config: { key, href } — the label/why copy lives in i18n (firstRun.*).
// ONB-1: "privacy" (consent) and "photos" (reference images) are SEPARATE steps —
// each links to the surface where it is actually completed. The old compound
// "privacy" step linked only to consent but required photos too (uploaded on the
// profile page), so it never cleared after consenting.
export const TEACHER_STEP_CONFIG = [
  { key: "profile", href: "/my-profile" },
  { key: "privacy", href: "/consent" },
  { key: "photos", href: "/my-profile#privacy-reference-images" },
  // RECORD-ENTRY-1 — THE OVERRIDE IS GONE, exactly as its own note said it would be: "the
  // recorder returns in RECORD-1 and this override with it." While the recorder was withdrawn this
  // step's title was forced to an upload-only heading, because the derived
  // firstRun.teacher.record.title ("Record or upload your first lesson") offered something the
  // build could not do. It can again, so the honest title comes back.
  //
  // THE HREF STAYS /record, AND THAT IS A DELIBERATE ASYMMETRY I HAVE FLAGGED rather than hidden:
  // the title now names both actions while this link offers only upload. Pointing it at the
  // recorder would need the gate, and this module is a static config with no user — the gate would
  // have to come through deriveTeacherSteps and its two hooks, which is more surgery than a
  // checklist link warrants in this PR. A link to an unregistered route is the one outcome worse
  // than an incomplete one. Recording is one tap from Home, which is what the order required.
  { key: "record", href: "/record" },
  { key: "feedback", href: "/my-lessons" },
];

export const ADMIN_STEP_CONFIG = [
  { key: "teachers", href: "/teachers" },
  // F14 SETUP-LOOP-1 — was /frameworks, which is a HomeRedirect (App.js) and the dead link ONB-2
  // retired. Now the page that actually SETS a recording schedule, inside the admin shell.
  { key: "schedule", href: "/admin/schedule" },
  { key: "record", href: "/observation/new" },
  { key: "feedback", href: "/reports" },
];

export function deriveTeacherSteps(data = {}) {
  const readiness = data.readiness || {};
  // "record" is done once any lesson exists; "feedback" once one has been reviewed.
  const hasLesson =
    Boolean(data.latest_lesson) ||
    (typeof data.lessons_count === "number" ? data.lessons_count > 0 : false);
  const hasReviewed =
    (typeof data.reviewed_lessons === "number" ? data.reviewed_lessons > 0 : false) ||
    data.latest_lesson?.status === "reviewed";
  // ONB-1: reference photos may be optional in a given org (PRIVACY_REQUIRE_PROFILE).
  // The backend exposes `privacy_reference_images_required`; a MISSING field is treated
  // as required=true (safe default, matches today) so the step is never falsely "done".
  const photosRequired = readiness.privacy_reference_images_required !== false;
  const done = {
    profile: Boolean(readiness.teacher_profile_complete),
    // Consent alone — completes on the consent page it links to.
    privacy: Boolean(readiness.consent_complete),
    // Reference photos — the recognizer's real threshold, or auto-done where photos aren't required.
    photos: !photosRequired || Boolean(readiness.privacy_reference_images_ready),
    record: hasLesson,
    feedback: Boolean(hasReviewed),
  };
  return TEACHER_STEP_CONFIG.map((step) => ({ ...step, done: Boolean(done[step.key]) }));
}

export function deriveAdminSteps(status = {}) {
  const steps = status.onboarding_steps || {};
  const done = {
    teachers: Boolean(steps.first_teacher_added),
    schedule: Boolean(steps.observation_settings_configured),
    record: Boolean(steps.first_video_uploaded),
    feedback: Boolean(steps.first_analysis_complete),
  };
  return ADMIN_STEP_CONFIG.map((step) => ({ ...step, done: Boolean(done[step.key]) }));
}

// role: "teacher" | "admin". payload: the role's already-fetched data.
export function deriveFirstRunSteps(role, payload) {
  const steps = role === "teacher" ? deriveTeacherSteps(payload) : deriveAdminSteps(payload);
  const completeCount = steps.filter((s) => s.done).length;
  const allDone = completeCount === steps.length;
  return {
    role,
    steps,
    completeCount,
    total: steps.length,
    allDone,
    // First-run is "active" until every step is done (or the user dismisses it).
    active: !allDone,
  };
}

// ONB-2 — the GUIDED SETUP GATE. The setup PREREQUISITES (must be done before the platform is
// useful) vs the USAGE steps (first activity — they auto-complete AS you use the platform and are
// shown only as passive progress, never gated). Teacher: profile + consent + photos-where-required.
// Admin: add teachers + configure observation settings. `record`/`feedback` are usage for both.
// F14 SETUP-LOOP-1 — admin steps whose own href reaches the page that COMPLETES them, and which
// therefore open that page instead of the checklist. An allow-list, so a step added later keeps
// today's safe behaviour (the checklist) until somebody has checked its href actually lands
// somewhere that can finish it. That check is the whole of F14's "no step links to a page that
// lacks it".
export const ADMIN_DIRECT_STEP_KEYS = Object.freeze(["schedule"]);

export const REQUIRED_STEP_KEYS = {
  teacher: ["profile", "privacy", "photos"],
  admin: ["teachers", "schedule"],
};

// Where each role LANDS post-login while setup is incomplete (a navigable default, never a wall):
// admin → the existing /onboarding SetupChecklist; teacher → /my-profile with its instructional header.
export const SETUP_SURFACE = { teacher: "/my-profile", admin: "/onboarding" };

// W40 (C-270 / finding A4) — the banner's required admin rows map to the server setup object's live
// step ids, so a row's checkmark comes from the same source as the ratio (not the stale, only-ever-
// set onboarding_steps booleans). The usage rows (record/feedback) are not setup-required and keep
// their own state. Teacher rows have no setup step and are unaffected (no setup object on that path).
const SETUP_STEP_ID_BY_KEY = { teachers: "teachers_added", schedule: "recording_schedule_default" };

export function deriveSetupGate(role, payload) {
  const base = deriveFirstRunSteps(role, payload);
  const requiredKeys = REQUIRED_STEP_KEYS[role] || [];
  const surface = SETUP_SURFACE[role];
  // W40 (finding A/A4) — the ratio, completeness AND the required rows' done all come from the ONE
  // server setup derivation (`setup` in the onboarding-status payload), never a recount of the stale
  // onboarding_steps booleans. Fall back to the computed steps when no setup object is present (e.g.
  // the teacher dashboard), so the teacher gate is unchanged.
  const serverSetup = payload && payload.setup;
  const hasServerSetup = Boolean(serverSetup) && typeof serverSetup.required_total === "number";
  const setupDoneById = {};
  if (hasServerSetup) for (const s of serverSetup.steps || []) setupDoneById[s.id] = Boolean(s.done);
  const steps = base.steps.map((s) => {
    const setupId = SETUP_STEP_ID_BY_KEY[s.key];
    const done = hasServerSetup && setupId && setupId in setupDoneById ? setupDoneById[setupId] : s.done;
    return {
      ...s,
      done,
      required: requiredKeys.includes(s.key),
      // ONB-2 sent EVERY admin step to the /onboarding checklist, because the per-step hrefs were
      // not all real (the old /frameworks link among them). F14 SETUP-LOOP-1: a step whose href DOES
      // reach the page that completes it must open that page, not the checklist that says it is
      // outstanding. `schedule` is the first such step; the rest still go to the surface, so this
      // narrows the override rather than removing it.
      //
      // Teacher step hrefs are all real routes (/my-profile, /consent, …) and are kept as-is.
      href: role === "admin" && !ADMIN_DIRECT_STEP_KEYS.includes(s.key) ? surface : s.href,
    };
  });
  const requiredSteps = steps.filter((s) => s.required);
  const usageSteps = steps.filter((s) => !s.required);
  const requiredDoneCount = hasServerSetup ? serverSetup.required_done : requiredSteps.filter((s) => s.done).length;
  const requiredTotal = hasServerSetup ? serverSetup.required_total : requiredSteps.length;
  // The GATE clears when every REQUIRED step is done (usage steps never gate).
  const requiredComplete = hasServerSetup ? Boolean(serverSetup.complete) : requiredSteps.every((s) => s.done);
  return {
    ...base,
    steps,
    requiredSteps,
    usageSteps,
    requiredComplete,
    requiredDoneCount,
    requiredTotal,
    setupHref: SETUP_SURFACE[role],
  };
}
